import { TextButton as Button, COLORS, RadioButton, Size, SystemIcons, Textarea } from '@laerdal/life-react-components';
import { useState } from 'react';
import styled from 'styled-components';
import ArticleApi from '../services/api/ArticleApi';
import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';

const Outline = styled.div`
  

  display: flex;
  padding: 32px ;
  gap: 24px;
  border-radius: 8px ;
    
  background: var(--life-color-bg-surface-subtle);
  justify-content: center;

`

const Containter = styled.div`
  with: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  gap: 0;
  width: 100%;
`

const Text = styled.div`
  color: var(--life-color-text-default);
  text-align: center;
  font-feature-settings: 'liga' off;

  /* L - Desktop/H6 */
  font-family: Lato;
  font-size: 25px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%; /* 30px */
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 24px;
`

const SubText = styled.div`
  color: ${COLORS.neutral_600};
  text-align: center;
  font-feature-settings: 'liga' off;
  font-family: Lato;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  margin-top: 16px;
`

const Line = styled.div`
  height: 1px;
  width: 100%;
  border-top: 1px solid ${COLORS.neutral_200};
  margin: 16px 0;
`

const SecondPhase = styled.div`
  with: 100%;
  display: flex;
  flex-direction: column;
  align-items: start;
  align-self: stretch;
  gap:8px;
  width: 100%;
`

const ThirdPhase = styled.div`
  with: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  gap:8px;
  width: 100%;
`

const CheckContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 64px;
  height: 64px;
  gap: 8px;
  border-radius: 64px;
  border: 2px;
  border: 1px solid ${COLORS.positive_500};
`

const FeedbackButton = styled(Button)`
  && > div {
    background-color: ${COLORS.primary_50};
    color: ${COLORS.primary_500};
  }
  &&:hover > div {
    background-color: ${COLORS.primary_50};
    color: ${COLORS.primary_500};
    filter: brightness(0.95);
  }
  &&:active > div {
    background-color: ${COLORS.primary_50};
    color: ${COLORS.primary_500};
    filter: brightness(0.9);
  }
`;

const StyledCheck = styled(SystemIcons.CheckMark)`
  color: ${COLORS.positive_500};
`


const TextAreaContainer = styled.div`
  width: 100%;

  div {
    height: 200px;

    input {
      height: 200px;
    }
  }
`

const CustomTextarea = styled(Textarea)`
  width: 100%;
  height: 120px;
`


interface FaqProps {
  articleNumber: string,
  positiveFeedbackCount: number;
}

const ArticleFeedback = ({ articleNumber, positiveFeedbackCount }: FaqProps) => {
  const { t } = useTranslation(['articles', 'translation']);

  const [phase, setPhase] = useState<number>(1);
  const [helpfull, setHelpfull] = useState<boolean | undefined>(undefined);
  const [moreInfoOption, setMoreInfoOption] = useState<string>('');
  const [moreInfoText, setMoreInfoText] = useState<string>('');


  const helpfullClick = (helpful: boolean) => {
    posthog.capture(helpful ? 'SUPP-ArticleFeedback-Helpful' : 'SUPP-ArticleFeedback-NotHelpful', {
      articleNumber: articleNumber,
    });
    setMoreInfoOption('')
    setHelpfull(helpful);
    if (helpful) setPhase(2);
    else setPhase(3);
  }

  const submitClick = () => {
    posthog?.capture?.('SUPP-ArticleFeeedback',
      {
        articleNumber: articleNumber,
        helpfull: helpfull,
        moreInfoOption: moreInfoOption,
        moreInfoText: moreInfoText
      }
    );

    ArticleApi.PostFeedback(articleNumber, helpfull)
    setPhase(4);
  }

  return (
    <Outline>
      <Containter>
        {phase < 4 &&
          <>
            <Text>{t('Was this article helpful?')}</Text>
            <ButtonContainer>
              {/* TODO: verify how to do the dark theme */}
              <FeedbackButton icon={<SystemIcons.ThumbsUp />} variant='primary' onClick={() => helpfullClick(true)}>{t('Yes', { ns: 'translation' })}</FeedbackButton>
              <FeedbackButton icon={<SystemIcons.ThumbsDown />} variant='primary' onClick={() => helpfullClick(false)}>{t('No', { ns: 'translation' })}</FeedbackButton>
            </ButtonContainer>

            {positiveFeedbackCount > 0 &&
              <SubText>
                {t('{{positiveFeedbackCount}} found this helpful', { positiveFeedbackCount, ns: 'articles' })}
              </SubText>
            }
          </>
        }

        {phase == 2 &&
          <SecondPhase>
            <Line />
            <Text>{t('Got a second? Tell us what we’re doing well.', { ns: 'translation' })}</Text>
            <RadioButton
              id="DemoRadioButton"
              selected={moreInfoOption == 'The content was clear and easy to understand.'}
              select={() => {
                setMoreInfoOption('The content was clear and easy to understand.');
              }}
              label={t('The content was clear and easy to understand.', { ns: 'translation' })} />
            <RadioButton
              id="DemoRadioButton2"
              selected={moreInfoOption == 'The article solved my problem or answered my question.'}
              select={() => {
                setMoreInfoOption('The article solved my problem or answered my question.');
              }}
              label={t('The article solved my problem or answered my question.', { ns: 'translation' })} />
            <RadioButton
              id="DemoRadioButton3"
              selected={moreInfoOption == 'The step-by-step instructions were helpful.'}
              select={() => {
                setMoreInfoOption('The step-by-step instructions were helpful.');
              }}
              label={t('The step-by-step instructions were helpful.', { ns: 'translation' })} />
            <RadioButton
              id="DemoRadioButton4"
              selected={moreInfoOption == 'Other'}
              select={() => {
                setMoreInfoOption('Other');
              }}
              label={t('Other', { ns: 'translation' })} />

            <CustomTextarea
              placeholder={t('Tell us more...', { ns: 'translation' })}
              size={Size.Medium}
              id='textarea3'
              onChange={value => setMoreInfoText(value)} />
            <ButtonContainer>
              <Button variant='primary' onClick={() => submitClick()}>{t('Submit', { ns: 'translation' })}</Button>
              <Button variant='secondary' onClick={() => submitClick()}>{t('Skip', { ns: 'translation' })}</Button>
            </ButtonContainer>
          </SecondPhase>
        }

        {phase == 3 &&
          <SecondPhase>
            <Line />
            <Text>{t('Sorry about that, can you tell us more?', { ns: 'translation' })}</Text>
            <RadioButton
              id="DemoRadioButton5"
              selected={moreInfoOption == 'The content was outdated.'}
              select={() => {
                setMoreInfoOption('The content was outdated.');
              }}
              label={t('The content was outdated.', { ns: 'translation' })} />
            <RadioButton
              id="DemoRadioButton6"
              selected={moreInfoOption == 'The article didn’t address my problem or question.'}
              select={() => {
                setMoreInfoOption('The article didn’t address my problem or question.');
              }}
              label={t('The article didn’t address my problem or question.', { ns: 'translation' })} />
            <RadioButton
              id="DemoRadioButton7"
              selected={moreInfoOption == 'The instructions were incomplete or incorrect.'}
              select={() => {
                setMoreInfoOption('The instructions were incomplete or incorrect.');
              }}
              label={t('The instructions were incomplete or incorrect.', { ns: 'translation' })} />
            <RadioButton
              id="DemoRadioButton8"
              selected={moreInfoOption == 'The AI translation is not accurate.'}
              select={() => {
                setMoreInfoOption('The AI translation is not accurate.');
              }}
              label={t('The AI translation is not accurate.', { ns: 'translation' })} />
            <RadioButton
              id="DemoRadioButton9"
              selected={moreInfoOption == 'Other'}
              select={() => {
                setMoreInfoOption('Other');
              }}
              label={t('Other', { ns: 'translation' })} />

            <CustomTextarea
              placeholder={t('Is there anything we can do to improve?', { ns: 'translation' })}
              size={Size.Medium}
              id='textarea'
              onChange={value => setMoreInfoText(value)} />

            <ButtonContainer>
              <Button variant='primary' onClick={() => submitClick()}>{t('Submit', { ns: 'translation' })}</Button>
              <Button variant='secondary' onClick={() => submitClick()}>{t('Skip', { ns: 'translation' })}</Button>
            </ButtonContainer>
          </SecondPhase>
        }

        {phase == 4 &&
          <ThirdPhase>
            <CheckContainer>
              <StyledCheck size='32px' />
            </CheckContainer>
            <Text>{t('Thank you for your feedback', { ns: 'translation' })}</Text>
          </ThirdPhase>
        }
      </Containter>
    </Outline>
  );
}

export default ArticleFeedback;

